import { call, callForm } from "../../util/action";

export function buildListUrl(descriptor, { page = 1, search = "", filters = {}, ordering = "" } = {}) {
  const parts = [`page=${page}`];
  parts.push("page_size=25"); // keep in sync with ResourceTable PAGE_SIZE (matches API StandardResultsSetPagination default)
  if (search) parts.push(`search=${encodeURIComponent(search)}`);
  if (ordering) parts.push(`ordering=${encodeURIComponent(ordering)}`);
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") parts.push(`${k}=${encodeURIComponent(v)}`);
  });
  return `${descriptor.endpoint}?${parts.join("&")}`;
}

export function fetchList(descriptor, params, cb) {
  call({ method: "GET", url: buildListUrl(descriptor, params) }, r => {
    if (Array.isArray(r)) return cb({ results: r, count: r.length });
    if (r && Array.isArray(r.results)) return cb({ results: r.results, count: r.count ?? r.results.length });
    cb({ results: [], count: 0 });
  });
}

export function fetchItem(descriptor, id, cb) {
  call({ method: "GET", url: `${descriptor.endpoint}${id}/` }, cb);
}

export function saveItem(descriptor, item, me, cb) {
  const isNew = !item.id;
  let payload = { ...item };
  if (isNew && typeof descriptor.injectOnCreate === "function") payload = descriptor.injectOnCreate(payload, me);
  (descriptor.fields || []).forEach(f => {
    if (f.type !== "entity") return;
    const v = payload[f.name];
    if (v == null) return;
    if (Array.isArray(v)) {
      payload[f.name] = v.map(x => (x && typeof x === "object") ? (x.id ?? x.value ?? x) : x);
    } else if (typeof v === "object") {
      payload[f.name] = v.id ?? v.value ?? v;
    }
  });

  // Image fields: only resend a File (new upload) or "" (explicit clear).
  // An unchanged value is a URL string (or null) fetched from GET — the API can't accept that back.
  (descriptor.fields || []).forEach(f => {
    if (f.type !== "image") return;
    const v = payload[f.name];
    if (typeof File !== "undefined" && v instanceof File) return;
    if (v === "") return;
    delete payload[f.name];
  });

  const hasFile = Object.values(payload).some(v => typeof File !== "undefined" && v instanceof File);

  if (hasFile) {
    const fd = new FormData();
    Object.entries(payload).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      if (typeof File !== "undefined" && v instanceof File) fd.append(k, v);
      else if (Array.isArray(v)) v.forEach(item => fd.append(k, item));
      else if (typeof v === "object") fd.append(k, JSON.stringify(v));
      else fd.append(k, v);
    });
    callForm({ method: isNew ? "POST" : "PATCH", url: isNew ? descriptor.endpoint : `${descriptor.endpoint}${item.id}/`, post: fd }, cb);
    return;
  }

  call(
    {
      method: isNew ? "POST" : "PATCH",
      url: isNew ? descriptor.endpoint : `${descriptor.endpoint}${item.id}/`,
      post: payload,
    },
    cb
  );
}

export function deleteItem(descriptor, id, cb) {
  call({ method: "DELETE", url: `${descriptor.endpoint}${id}/` }, r => cb(!!r && r.status === 204));
}

export function patchItem(descriptor, id, data, cb) {
  call({ method: "PATCH", url: `${descriptor.endpoint}${id}/`, post: data }, cb);
}

// Normalizes a descriptor's deactivation config, whether declared via the new
// `deactivation` shape or the legacy `softDelete: true` shorthand.
export function getDeactivation(descriptor) {
  if (descriptor.deactivation) {
    const d = descriptor.deactivation;
    return {
      field: d.field,
      activeValue: d.activeValue,
      inactiveValue: "inactiveValue" in d ? d.inactiveValue : !d.activeValue,
      via: d.via || "patch",
    };
  }
  if (descriptor.softDelete) {
    return { field: "is_removed", activeValue: false, inactiveValue: true, via: "delete" };
  }
  return null;
}

export function isInactive(item, cfg) {
  return cfg ? item[cfg.field] !== cfg.activeValue : false;
}

// A 400 comes back as a raw Response (see action.js). Parse field errors best-effort.
export async function parseErrors(response) {
  const out = { __ok: false, fields: {}, message: "" };
  try {
    const body = await response.json();
    if (body && typeof body === "object") {
      Object.entries(body).forEach(([k, v]) => {
        out.fields[k] = Array.isArray(v) ? v.join(", ") : String(v);
      });
      out.message = Object.values(out.fields).join("; ");
    }
  } catch (e) { /* non-JSON body */ }
  return out;
}
